/**
 * Счёт конторы прямо из сита — то же, что под карандашом в студии: состояние
 * счёта, баланс, фрибет, пометка, зеркало, прокси с проверкой, «не беру».
 * Баланс уходит в кассу (`saveBalance`), фрибет — в настройки ленты: нога
 * этой конторы считается SNR и не выходит за вписанную сумму. Остальное —
 * в `useAccounts`, поэтому студия, сито, бот и касса видят одно и то же.
 */
import { PRIVATE_MIRROR } from '../lib/feedContext';
import { Decimal } from 'decimal.js';
import { useEffect, useRef, useState } from 'react';

import { doors as publishDoors, roadProbe, roadSecret } from '../confirm/publish';
import { parseOk, reportOf } from '../confirm/roads';
import { saveBalance } from '../access/balance';
import { useSettings } from '../store/settings';
import { type Standing, flushRoads, pressureOf, useAccounts } from './accounts';
import { applyDoor, doorInputOf, formatDoor, homeOf, knownDoors, parseDoor, urlFor } from './hosts';
import { venueKind } from './meta';
import { extFresh, extSeen } from './permit';
import { useScreen } from './store';
import { parseFailText, parseRoadLine } from './roads';
import { logoOf } from './venues';

const STAND: readonly { code: Standing; name: string; says: string }[] = [
  { code: 'open', name: 'работает', says: 'лента показывает такие вилки как обычно' },
  { code: 'limited', name: 'порезан', says: 'лента пометит такие вилки: в порезанной конторе вторую ногу часто берут мелкой суммой' },
  { code: 'closed', name: 'закрыт', says: 'лента не показывает их вовсе и говорит, сколько скрыла' },
];

function proxyHint(): string {
  const seen = extSeen();
  if (seen === null || seen === '') return 'нет карточки FORQ — касса откроется с дома';
  if (!extFresh(seen)) return 'карточка старая — обновите, строка уже записана';
  return '';
}

function ownHost(code: string): string | null {
  const home = homeOf(code);
  if (home !== null) {
    try {
      return new URL(home).hostname.replace(/^www\./, '');
    } catch {
      /* ниже — известные имена */
    }
  }
  return knownDoors(code)[0] ?? null;
}

function askDoor(origin: string): void {
  const map: Record<string, string> = {};
  for (const [venue, one] of Object.entries(useAccounts.getState().accounts)) {
    if (one.door !== '') map[venue] = one.door;
  }
  publishDoors(map, origin.endsWith('/*') ? origin : `${origin}/*`);
}

export function BookEditor({
  code,
  name,
  lastUrl,
  onClose,
}: {
  code: string;
  name: string;
  lastUrl?: string | null;
  onClose: () => void;
}): React.ReactElement {
  const account = useAccounts((s) => s.accounts[code]);
  const uses = useAccounts((s) => s.uses);
  const cap = useSettings((s) => s.caps[code]);
  const capCur = useSettings((s) => s.capsCur[code]);
  const freebet = useSettings((s) => s.freebets?.[code]);
  const freebetCur = useSettings((s) => s.freebetsCur?.[code]);
  const fx = useSettings((s) => s.fx);
  const meta = useScreen((s) => s.meta);
  // Баланс лежит в валюте счёта в кассе; нет счёта — в валюте кассы конторы.
  // Вводят в той же валюте: перевод курсом уже не тот остаток, что на сайте.
  const stored: 'rub' | 'usd' =
    capCur !== undefined ? (capCur === 'RUB' ? 'rub' : 'usd') : venueKind(meta, code) === 'rub' ? 'rub' : 'usd';
  const [unit, setUnit] = useState<'rub' | 'usd'>(stored);
  const fbStored: 'rub' | 'usd' =
    freebetCur !== undefined ? (freebetCur === 'RUB' ? 'rub' : 'usd') : stored;
  const [fbUnit, setFbUnit] = useState<'rub' | 'usd'>(fbStored);
  const [capSay, setCapSay] = useState('');
  const shown = (value: number | undefined, as: 'rub' | 'usd', from: 'rub' | 'usd' = stored): string => {
    if (value === undefined) return '';
    if (as === from || !(fx > 0)) return String(value);
    const out = as === 'usd' ? new Decimal(value).div(fx) : new Decimal(value).times(fx);
    return as === 'usd' ? out.toDecimalPlaces(2).toString() : out.round().toString();
  };
  const standing = account?.standing ?? 'open';
  const [door, setDoor] = useState(doorInputOf(account?.door ?? ''));
  const [proxy, setProxy] = useState(account?.proxy ?? '');
  const [capText, setCapText] = useState(shown(cap, stored));
  const [fbText, setFbText] = useState(shown(freebet, fbStored, fbStored));
  const saveCap = (): void => {
    const text = capText.replace(/\s/g, '').replace(',', '.');
    const was = useSettings.getState().caps[code];
    const cur = unit === 'rub' ? 'RUB' : 'USD';
    if (text === '') {
      if (was === undefined) return;
      void saveBalance(code, null, cur, name).then((say) => setCapSay(say ?? ''));
      return;
    }
    let value: Decimal;
    try {
      value = new Decimal(text);
    } catch {
      return;
    }
    if (!value.isFinite() || value.lte(0)) return;
    const amount = value.toDecimalPlaces(2, Decimal.ROUND_DOWN);
    if (was !== undefined && unit === stored && new Decimal(was).eq(amount)) return;
    setCapSay('');
    void saveBalance(code, amount.toString(), cur, name).then((say) => setCapSay(say ?? ''));
  };
  const saveFree = (): void => {
    const text = fbText.replace(/\s/g, '').replace(',', '.');
    const cur = fbUnit === 'rub' ? 'RUB' : 'USD';
    if (text === '') {
      useSettings.getState().setFreebet(code, null);
      return;
    }
    let value: Decimal;
    try {
      value = new Decimal(text);
    } catch {
      return;
    }
    if (!value.isFinite() || value.lte(0)) return;
    const amount = value.toDecimalPlaces(2, Decimal.ROUND_DOWN);
    const was = useSettings.getState().freebets[code];
    if (was !== undefined && fbUnit === fbStored && new Decimal(was).eq(amount)) return;
    useSettings.getState().setFreebet(code, amount.toNumber(), cur);
  };
  const [proxySay, setProxySay] = useState(proxyHint());
  const [probing, setProbing] = useState(false);
  const nonce = useRef(0);
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close.current();
    };
    window.addEventListener('keydown', onKey, true);
    // Один раз, в поле баланса. `onClose` с сита — новая функция на каждый кадр
    // ленты: если слушать её в deps, фокус прыгает на «работает», и цифры
    // в баланс не входят (1–3 ещё и открывают ноги вилки).
    box.current?.querySelector<HTMLInputElement>('input.nx-bked__cap')?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey, true);
    };
  }, []);

  useEffect(() => {
    const hear = (event: MessageEvent): void => {
      const ok = parseOk(event.data);
      if (ok === null) return;
      if (ok.phase === 'apply') {
        if (ok.say) setProxySay(ok.say);
        return;
      }
      if (ok.phase !== 'probe') return;
      if (ok.nonce !== undefined && nonce.current !== 0 && ok.nonce !== nonce.current) return;
      const row = ok.rows.find((one) => one.venue === code);
      if (!row && !ok.say) return;
      setProbing(false);
      setProxySay(row ? reportOf(row) : ok.say);
    };
    window.addEventListener('message', hear);
    return () => window.removeEventListener('message', hear);
  }, [code]);

  const doorBad = door.trim() !== '' && parseDoor(door) === null;
  const writeDoor = (raw: string, ask: boolean): void => {
    const parsed = parseDoor(raw);
    if (raw.trim() !== '' && parsed === null) return;
    useAccounts.getState().setDoor(code, parsed ? formatDoor(parsed) : '');
    if (ask && parsed) askDoor(parsed.origin);
  };

  const proxyParsed = proxy.trim() === '' ? null : parseRoadLine(proxy);
  const proxyBad = proxyParsed !== null && !proxyParsed.ok;
  const writeProxy = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed !== '' && !parseRoadLine(trimmed).ok) {
      const parsed = parseRoadLine(trimmed);
      if (!parsed.ok) setProxySay(parseFailText(parsed.reason));
      return;
    }
    useAccounts.getState().setProxy(code, trimmed);
    roadSecret(code, trimmed);
    flushRoads(useAccounts.getState().accounts);
  };

  const probeDoor = (): void => {
    const chosen = parseDoor(door) ?? parseDoor(account?.door ?? '');
    const base = urlFor(code, lastUrl ?? null) ?? homeOf(code);
    const url = applyDoor(base, chosen) ?? (chosen ? `${chosen.origin}/` : homeOf(code));
    if (url && chosen) askDoor(chosen.origin);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const probeProxy = (): void => {
    writeProxy(proxy);
    if (!extSeen()) {
      setProxySay('нет карточки FORQ');
      return;
    }
    nonce.current += 1;
    const n = nonce.current;
    setProbing(true);
    setProxySay('проверяю выход');
    roadProbe(code, n);
    window.setTimeout(() => {
      if (nonce.current !== n) return;
      setProbing((wait) => {
        if (wait) setProxySay('нет ответа');
        return false;
      });
    }, 20_000);
  };

  const load = pressureOf(uses, code, Date.now());
  const omitted = account?.exclude ?? [];
  const logo = logoOf(code);

  return (
    <div
      className="nx-bked-veil"
      role="dialog"
      aria-modal="true"
      aria-label={`ваш счёт в ${name}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="nx-bked" ref={box}>
        <div className="nx-bked__h">
          {logo !== null && <img className="nx-bk__i" src={logo} alt="" />}
          <b>ваш счёт в {name}</b>
          <button type="button" className="nx-bked__x" onClick={onClose}>
            готово
          </button>
        </div>

        <div className="nx-bked__r">
          <u>счёт</u>
          <div className="nx-pick" role="group" aria-label="состояние счёта">
            {STAND.map((one) => (
              <button
                key={one.code}
                type="button"
                className={standing === one.code ? 'on' : ''}
                aria-pressed={standing === one.code}
                onClick={() => useAccounts.getState().setStanding(code, one.code)}
              >
                {one.name}
              </button>
            ))}
          </div>
        </div>
        <p className="nx-bked__d">{STAND.find((one) => one.code === standing)?.says}</p>

        <div className="nx-bked__r">
          <u>баланс</u>
          <div className="nx-bked__line">
            <input
              type="text"
              inputMode="decimal"
              className="nx-bked__cap"
              placeholder="не вписан"
              aria-label={`баланс, ${unit === 'usd' ? 'долларов' : 'рублей'}: ${name}`}
              value={capText}
              onChange={(event) => setCapText(event.target.value.replace(/[^\d.,\s]/g, ''))}
              onBlur={saveCap}
              onKeyDown={(event) => {
                if (event.key === 'Enter') saveCap();
              }}
            />
            <button
              type="button"
              className="nx-bked__cur"
              title={unit === 'usd' ? 'в рублях' : 'в долларах'}
              aria-label={`валюта счёта: ${unit === 'usd' ? 'доллары' : 'рубли'}, нажмите, чтобы сменить`}
              onClick={() => {
                const next = unit === 'usd' ? 'rub' : 'usd';
                setUnit(next);
                setCapText(shown(useSettings.getState().caps[code], next));
              }}
            >
              {unit === 'usd' ? '$' : '₽'}
            </button>
            <span>столько на счёте — обе ноги вилки урежем, чтобы хватило</span>
          </div>
        </div>
        {capSay !== '' && <p className="nx-bked__d warn">{capSay}</p>}

        <div className="nx-bked__r">
          <u>фрибет</u>
          <div className="nx-bked__line">
            <input
              type="text"
              inputMode="decimal"
              className="nx-bked__cap"
              placeholder="не вписан"
              aria-label={`фрибет, ${fbUnit === 'usd' ? 'долларов' : 'рублей'}: ${name}`}
              value={fbText}
              onChange={(event) => setFbText(event.target.value.replace(/[^\d.,\s]/g, ''))}
              onBlur={saveFree}
              onKeyDown={(event) => {
                if (event.key === 'Enter') saveFree();
              }}
            />
            <button
              type="button"
              className="nx-bked__cur"
              title={fbUnit === 'usd' ? 'в рублях' : 'в долларах'}
              aria-label={`валюта фрибета: ${fbUnit === 'usd' ? 'доллары' : 'рубли'}, нажмите, чтобы сменить`}
              onClick={() => {
                const next = fbUnit === 'usd' ? 'rub' : 'usd';
                setFbUnit(next);
                setFbText(shown(useSettings.getState().freebets[code], next, fbStored));
              }}
            >
              {fbUnit === 'usd' ? '$' : '₽'}
            </button>
            <span>ставка не из кармана — вилки этой конторы посчитаем как фрибет</span>
          </div>
        </div>

        <div className="nx-bked__r">
          <u>пометка</u>
          <input
            type="text"
            className="nx-bked__wide"
            placeholder="режут после 3к, только с телефона…"
            aria-label={`пометка про счёт: ${name}`}
            value={account?.note ?? ''}
            onChange={(event) => useAccounts.getState().setNote(code, event.target.value)}
          />
        </div>

        <div className="nx-bked__r">
          <u>{PRIVATE_MIRROR ? 'адрес сайта' : 'зеркало'}</u>
          <div className="nx-bked__col">
            <div className="nx-bked__line">
              <input
                type="text"
                className={`nx-bked__wide${doorBad ? ' bad' : ''}`}
                spellCheck={false}
                placeholder={`${ownHost(code) ?? 'домен конторы'} или свой домен`}
                aria-label={`${PRIVATE_MIRROR ? 'адрес сайта' : 'зеркало конторы'}: ${name}`}
                value={door}
                onChange={(event) => {
                  setDoor(event.target.value);
                  writeDoor(event.target.value, false);
                }}
                onBlur={() => writeDoor(door, true)}
              />
              <button type="button" className="nx-bked__try" onClick={probeDoor}>
                открыть пробу
              </button>
            </div>
            {knownDoors(code).length > 0 && (
              <div className="nx-bked__chips">
                {knownDoors(code).map((host) => {
                  const now = parseDoor(door);
                  const on = now !== null && now.host === host && now.prefix === '';
                  return (
                    <button
                      key={host}
                      type="button"
                      className={on ? 'on' : ''}
                      onClick={() => {
                        setDoor(host);
                        writeDoor(host, true);
                      }}
                    >
                      {host}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="nx-bked__d">клик откроет событие на этом хосте, путь тот же</p>
          </div>
        </div>

        <div className="nx-bked__r">
          <u>прокси</u>
          <div className="nx-bked__col">
            <div className="nx-bked__line">
              <input
                type="text"
                className={`nx-bked__wide${proxyBad ? ' bad' : ''}`}
                spellCheck={false}
                placeholder="host:port:логин:пароль — http и socks"
                aria-label={`прокси конторы: ${name}`}
                value={proxy}
                onChange={(event) => setProxy(event.target.value)}
                onBlur={() => writeProxy(proxy)}
              />
              <button type="button" className="nx-bked__try" disabled={probing} onClick={probeProxy}>
                {probing ? 'проверяю' : 'проверить'}
              </button>
            </div>
            <p className={`nx-bked__d${probing ? ' wait' : ''}`}>
              {proxySay || 'клик откроет эту контору через этот адрес. на ленту не влияет'}
            </p>
          </div>
        </div>

        {omitted.length > 0 && (
          <div className="nx-bked__r">
            <u>не беру</u>
            <div className="nx-bked__col">
              {omitted.map((one, index) => (
                <div className="nx-bked__omit" key={`${one.ev}:${index}`}>
                  <span>{one.label || one.ev}</span>
                  <button
                    type="button"
                    className={one.keep ? 'on' : ''}
                    aria-pressed={one.keep === true}
                    title={one.keep ? 'этот матч у кассы не вернётся сам' : 'держать после ухода матча'}
                    onClick={() => useAccounts.getState().keepExclude(code, index, !one.keep)}
                  >
                    держу
                  </button>
                  <button
                    type="button"
                    aria-label="снять"
                    title="снять"
                    onClick={() => useAccounts.getState().dropExclude(code, index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {load.total > 0 && (
          <p className={`nx-bked__d${load.crowded ? ' warn' : ''}`}>
            {load.mine} из {load.total} ваших ставок за неделю прошли здесь
            {load.crowded ? '. это уже большинство' : ''}
          </p>
        )}
      </div>
    </div>
  );
}
